import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, Ticket, CheckCircle2, XCircle, Lightbulb, ShieldCheck, ChevronRight,
  X, Bot, Send, Trophy, ArrowRight, Minimize2, ListChecks, RotateCcw, GripVertical, ExternalLink,
} from "lucide-react";

const DIFF = {
  Beginner: "text-emerald-600", Intermediate: "text-amber-600", Engineer: "text-rose-600",
};

// render inline **bold** segments from the step text
function renderRich(text) {
  return String(text).split(/(\*\*.+?\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>);
}

export default function GuidedPanel({ open, initialScenario, onClose, setView, popout = false, onDockedChange }) {
  const { refresh } = useAzure();
  const [scenarios, setScenarios] = useState([]);
  const [sid, setSid] = useState(initialScenario || null);
  const [scn, setScn] = useState(null);
  const [prog, setProg] = useState(null);
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [tab, setTab] = useState("task"); // task | mentor
  const [pos, setPos] = useState(null); // {x,y} when floating; null = docked right
  const panelRef = useRef(null);
  const dragRef = useRef(null);

  const startDrag = (e) => {
    if (e.target.closest("button")) return; // don't drag when clicking controls
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    setPos({ x: rect.left, y: rect.top });
    const move = (ev) => {
      if (!dragRef.current) return;
      const W = panelRef.current?.offsetWidth || 380;
      const x = Math.max(4 - W + 120, Math.min(ev.clientX - dragRef.current.dx, window.innerWidth - 120));
      const y = Math.max(0, Math.min(ev.clientY - dragRef.current.dy, window.innerHeight - 48));
      setPos({ x, y });
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    e.preventDefault();
  };

  const loadList = useCallback(async () => {
    const { data } = await api.get("/guided/scenarios");
    setScenarios(data.scenarios);
  }, []);

  const startScenario = useCallback(async (id) => {
    const { data } = await api.post(`/guided/scenarios/${id}/start`);
    setScn(data.scenario); setProg(data.progress); setSid(id); setResult(null);
    await refresh(); // reflect any fault injection in the live portal
  }, [refresh]);

  const redoScenario = useCallback(async (id) => {
    const { data } = await api.post(`/guided/scenarios/${id}/reset`);
    setScn(data.scenario); setProg(data.progress); setSid(id); setResult(null); setTab("task");
    await refresh();
    loadList();
    toast.success("Lab reset — you can redo it from the first task");
  }, [refresh, loadList]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);
  useEffect(() => { if (open && initialScenario) startScenario(initialScenario); }, [open, initialScenario, startScenario]);
  useEffect(() => { onDockedChange?.(open && !popout && !pos); }, [open, popout, pos, onDockedChange]);
  useEffect(() => () => onDockedChange?.(false), [onDockedChange]);


  if (!open) return null;

  const taskStatus = (tid) => prog?.tasks?.[tid]?.status || "locked";
  const activeIdx = scn ? scn.tasks.findIndex((t) => taskStatus(t.id) === "active") : -1;
  const curIdx = activeIdx >= 0 ? activeIdx : (scn ? scn.tasks.length : 0);
  const task = scn ? scn.tasks[curIdx] || null : null;
  const completed = prog?.status === "completed";
  const nextRec = [...scenarios]
    .filter((s) => s.id !== sid && s.status !== "completed")
    .sort((a, b) => a.order - b.order)[0] || null;

  const validate = async () => {
    if (!task) return;
    setValidating(true); setResult(null);
    try {
      const { data } = await api.post(`/guided/scenarios/${sid}/tasks/${task.id}/validate`);
      setResult(data);
      const r = await api.get(`/guided/scenarios/${sid}`);
      setProg(r.data.progress);
      await refresh();
      if (r.data.progress?.status === "completed") loadList();
      if (data.passed) toast.success("Task validated");
    } catch (e) { toast.error(apiErr(e)); } finally { setValidating(false); }
  };

  const popOut = () => {
    const url = `${window.location.origin}/azure/guided${sid ? `?scenario=${sid}` : ""}`;
    window.open(url, "guided_lab_window", "popup,width=440,height=880,menubar=no,toolbar=no,location=no,status=no");
    onClose?.(); // avoid duplicate panels; the lab now lives in its own window
  };

  return (
    <div ref={panelRef}
      className={popout
        ? "fixed inset-0 z-40 flex flex-col bg-card"
        : pos
        ? "fixed w-full sm:w-[380px] z-40 flex flex-col border rounded-lg shadow-2xl bg-card overflow-hidden"
        : "fixed right-0 top-12 bottom-0 w-full sm:w-[380px] z-40 flex flex-col border-l shadow-2xl bg-card"}
      style={popout ? {} : (pos ? { borderColor: "var(--az-header-border)", left: pos.x, top: pos.y, height: "78vh", maxHeight: "78vh" } : { borderColor: "var(--az-header-border)" })}
      data-testid="az-guided-panel">
      {/* header (drag handle when floating) */}
      <div onPointerDown={popout ? undefined : startDrag} className={`flex items-center gap-2 px-3 py-3 text-white shrink-0 select-none ${popout ? "" : "cursor-move"}`} style={{ background: "#0e2a47" }} data-testid="az-guided-drag-handle" title={popout ? "" : "Drag to move this panel"}>
        {!popout && <GripVertical className="h-4 w-4 text-white/50 shrink-0" />}
        <GraduationCap className="h-5 w-5 text-[#50e6ff] shrink-0" />
        <span className="font-semibold text-sm truncate min-w-0 flex-1">Guided Cloud Engineering</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {!popout && (
            <button onClick={popOut} data-testid="az-guided-popout" title="Open this lab in a separate browser window you can drag to another screen"
              className="flex items-center gap-1 rounded-md bg-[#0078d4] hover:bg-[#2b88d8] px-2 py-1 text-[11px] font-semibold">
              <ExternalLink className="h-3.5 w-3.5" /> Pop out
            </button>
          )}
          {!popout && pos && <button className="text-white/70 hover:text-white text-[11px] font-medium px-1" onClick={() => setPos(null)} data-testid="az-guided-dock">Dock</button>}
          <button className="text-white/70 hover:text-white" onClick={() => (popout ? window.close() : onClose?.())} data-testid="az-guided-close"><X className="h-4 w-4" /></button>
        </div>
      </div>

      {/* scenario picker */}
      {!scn && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="az-guided-picker">
          <p className="text-xs text-muted-foreground">Pick a production ticket. You'll work in this real portal and validate your work as you go.</p>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => startScenario(s.id)} data-testid={`az-guided-pick-${s.id}`}
              className="w-full text-left rounded-lg border border-border p-3 hover:border-[#0078d4]/50">
              <div className="flex items-center gap-2 text-[11px]">
                <span className={`font-semibold ${DIFF[s.difficulty]}`}>{s.difficulty}</span>
                <span className="font-mono text-muted-foreground flex items-center gap-1"><Ticket className="h-3 w-3" />{s.ticket}</span>
                {s.status === "completed" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 ml-auto" />}
              </div>
              <div className="font-medium text-sm mt-1">{s.title}</div>
            </button>
          ))}
        </div>
      )}

      {scn && (
        <>
          {/* ticket strip */}
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 text-xs shrink-0">
            <span className="font-mono text-[#0078d4] flex items-center gap-1"><Ticket className="h-3.5 w-3.5" />{scn.ticket}</span>
            <span className="text-muted-foreground truncate">{scn.title}</span>
            <button className="ml-auto text-muted-foreground hover:text-[#0078d4] flex items-center gap-1" onClick={() => redoScenario(sid)} data-testid="az-guided-redo" title="Reset this lab and redo it from the first task">
              <RotateCcw className="h-3.5 w-3.5" /> Redo
            </button>
            <button className="text-muted-foreground hover:text-foreground flex items-center gap-1" onClick={() => { setScn(null); setSid(null); setResult(null); }} data-testid="az-guided-switch">
              <Minimize2 className="h-3.5 w-3.5" /> Labs
            </button>
          </div>

          {/* tabs */}
          <div className="flex text-xs border-b border-border shrink-0">
            <button className={`flex-1 py-2 ${tab === "task" ? "border-b-2 border-[#0078d4] text-[#0078d4] font-medium" : "text-muted-foreground"}`} onClick={() => setTab("task")} data-testid="az-guided-tab-task">Task</button>
            <button className={`flex-1 py-2 flex items-center justify-center gap-1 ${tab === "mentor" ? "border-b-2 border-[#0078d4] text-[#0078d4] font-medium" : "text-muted-foreground"}`} onClick={() => setTab("mentor")} data-testid="az-guided-tab-mentor"><Bot className="h-3.5 w-3.5" /> Mentor</button>
          </div>

          {tab === "task" ? (
            <div className="flex-1 overflow-y-auto p-4">
              {/* progress pills */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {scn.tasks.map((t, i) => {
                  const st = taskStatus(t.id);
                  return <span key={t.id} className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${
                    st === "passed" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                    : st === "active" ? "bg-[#0078d4]/10 text-[#0078d4] border-[#0078d4]/30"
                    : "bg-muted text-muted-foreground border-border"}`}>
                    {st === "passed" ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                  </span>;
                })}
              </div>

              {completed ? (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="az-guided-complete">
                  <div className="text-center">
                    <Trophy className="h-7 w-7 text-emerald-500 mx-auto" />
                    <div className="font-semibold mt-2">Ticket resolved!</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{scn.ticket} · {scn.tasks.length}/{scn.tasks.length} tasks</div>
                  </div>
                  <ul className="mt-3 text-xs space-y-1">
                    {scn.outcomes.map((o, i) => <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" /> {o}</li>)}
                  </ul>
                  {(scn.skills || []).length > 0 && (
                    <div className="mt-3" data-testid="az-guided-skills">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Skills earned</div>
                      <div className="flex flex-wrap gap-1.5">
                        {scn.skills.map((sk) => <span key={sk} className="text-[11px] px-2 py-0.5 rounded-full bg-[#0078d4]/10 text-[#0078d4] border border-[#0078d4]/25">{sk}</span>)}
                      </div>
                    </div>
                  )}
                  {nextRec ? (
                    <div className="mt-4 rounded-md border border-border bg-card p-3" data-testid="az-guided-next-rec">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Recommended next</div>
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className={`font-semibold ${DIFF[nextRec.difficulty]}`}>{nextRec.difficulty}</span>
                        <span className="font-mono text-muted-foreground flex items-center gap-1"><Ticket className="h-3 w-3" />{nextRec.ticket}</span>
                      </div>
                      <div className="font-medium text-sm mt-0.5">{nextRec.title}</div>
                      <Button size="sm" className="w-full mt-2.5 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={() => { setResult(null); startScenario(nextRec.id); }} data-testid="az-guided-start-next">
                        Start this lab <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-4 text-center text-xs text-emerald-600 font-medium" data-testid="az-guided-all-done">🎉 You've completed every guided lab. Outstanding work!</div>
                  )}
                  <Button size="sm" variant="outline" className="w-full mt-2 gap-1.5" onClick={() => redoScenario(sid)} data-testid="az-guided-redo-completed"><RotateCcw className="h-3.5 w-3.5" /> Redo this lab</Button>
                  <Button size="sm" variant="ghost" className="w-full mt-2 text-muted-foreground" onClick={() => { setScn(null); setSid(null); loadList(); }} data-testid="az-guided-back-list">Back to all labs</Button>
                </div>
              ) : task ? (
                <div data-testid="az-guided-task">
                  <div className="text-[11px] text-muted-foreground">Task {curIdx + 1} of {scn.tasks.length}</div>
                  <div className="font-semibold text-sm mt-0.5">{task.title}</div>
                  <p className="text-sm mt-1.5">{task.instruction}</p>
                  <div className="mt-2 rounded-md bg-muted/50 p-2.5 text-xs text-muted-foreground"><span className="font-medium text-foreground">Why: </span>{task.why}</div>

                  {(task.steps || []).length > 0 && (
                    <div className="mt-3 rounded-lg border border-[#0078d4]/25 bg-[#0078d4]/5 p-3" data-testid="az-guided-steps">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0078d4] mb-2">
                        <ListChecks className="h-4 w-4" /> Steps to complete
                      </div>
                      <ol className="space-y-1.5">
                        {task.steps.map((s, i) => (
                          <li key={i} className="flex gap-2 text-xs leading-relaxed" data-testid={`az-guided-step-${i}`}>
                            <span className="shrink-0 h-4 w-4 rounded-full bg-[#0078d4] text-white text-[10px] font-bold grid place-items-center mt-0.5">{i + 1}</span>
                            <span>{renderRich(s)}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {task.nav && setView && (
                    <button className="mt-3 text-xs text-[#0078d4] hover:underline flex items-center gap-1" data-testid="az-guided-goto" onClick={() => setView(task.nav)}>
                      Go to the relevant blade <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}

                  <Button className="w-full mt-3 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={validate} disabled={validating} data-testid="az-guided-validate">
                    <ShieldCheck className="h-4 w-4" /> {validating ? "Checking…" : "Validate my work"}
                  </Button>

                  {result && (
                    <div className={`mt-3 rounded-md border p-3 ${result.passed ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`} data-testid="az-guided-result">
                      <div className="flex items-center gap-1.5 text-sm font-semibold">
                        {result.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-rose-500" />}
                        {result.passed ? "Task completed" : "Not yet"}
                      </div>
                      <p className="text-xs mt-1">{result.message}</p>
                      {(result.expected || result.current) && (
                        <div className="mt-2 text-[11px] font-mono space-y-1">
                          {result.expected && <div className="rounded bg-emerald-500/10 px-2 py-1">Expected: {result.expected}</div>}
                          {result.current && <div className="rounded bg-rose-500/10 px-2 py-1">Current: {result.current}</div>}
                        </div>
                      )}
                      {result.hint && (
                        <div className="mt-2 flex items-start gap-1.5 text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-md p-2" data-testid="az-guided-hint">
                          <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span><strong>Hint {result.hint_level}:</strong> {result.hint}</span>
                        </div>
                      )}
                      {result.passed && result.next_task && (
                        <Button size="sm" className="mt-3 w-full gap-1.5 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={() => setResult(null)} data-testid="az-guided-next-task">
                          Next task <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <PanelMentor scenarioId={sid} taskId={task?.id} />
          )}
        </>
      )}
    </div>
  );
}

function PanelMentor({ scenarioId, taskId }) {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async () => {
    if (!q.trim() || busy) return;
    const question = q.trim();
    const history = msgs.map((m) => ({ role: m.role, text: m.text }));
    setMsgs((p) => [...p, { role: "student", text: question }]);
    setQ(""); setBusy(true);
    try {
      const { data } = await api.post("/guided/mentor", { scenario_id: scenarioId, task_id: taskId, question, history });
      setMsgs((p) => [...p, { role: "mentor", text: data.reply }]);
    } catch {
      setMsgs((p) => [...p, { role: "mentor", text: "I couldn't respond right now — try again shortly." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="az-guided-mentor">
      <div className="flex-1 overflow-y-auto p-3 space-y-2 text-sm">
        {msgs.length === 0 && <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">Ask me for direction — “Where should I look?”, “Why an NSG?”. I guide, I don't hand over the answer.</div>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.role === "student" ? "bg-[#0078d4] text-white" : "bg-muted"}`} data-testid={`az-mentor-${m.role}`}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="text-xs text-muted-foreground">Mentor is thinking…</div>}
        <div ref={endRef} />
      </div>
      <div className="p-2 border-t border-border flex gap-2 shrink-0">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Ask the mentor…" data-testid="az-mentor-input"
          className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
        <Button size="icon" className="bg-[#0078d4] hover:bg-[#106ebe]" onClick={ask} disabled={busy} data-testid="az-mentor-send"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
